/** @jsxImportSource theme-ui */
import { CompanyArticle, CompanyService } from '@/types/types';
import { ServicePricesState } from './servicePrices';
import { ExpandableServiceRow, ServicePricesCell } from './ServicePricesCell';
import { MISSING_VALUE, ValueCellProps, ValueOrMissingCell, YesNoCell } from './cells';

type ArticleRowCellProps = { row: { original: CompanyArticle } };

export const createServiceColumns = (servicePrices: ServicePricesState) => [
    ...serviceColumns,
    {
        id: 'Prices',
        columnKey: 'Prices',
        Header: 'Prices',
        accessor: (service: CompanyService) => service.Id,
        disableSortBy: true,
        Cell: ({ row }: { row: ExpandableServiceRow }) => (
            <ServicePricesCell row={row} servicePrices={servicePrices} />
        ),
    },
];

const serviceColumns = [
    { columnKey: 'Id', Header: 'Id', accessor: 'Id' },
    { columnKey: 'Name', Header: 'Name', accessor: 'Name' },
    { columnKey: 'Group', Header: 'Group', accessor: 'Group', Cell: ValueOrMissingCell },
    {
        columnKey: 'Duration',
        Header: 'Duration (min)',
        accessor: 'Duration',
        Cell: ValueOrMissingCell,
    },
    {
        columnKey: 'TotalSpots',
        Header: 'Total spots',
        accessor: 'TotalSpots',
        Cell: ValueOrMissingCell,
    },
    {
        columnKey: 'OnlyVisibleByAdmin',
        Header: 'Admin only',
        accessor: 'OnlyVisibleByAdmin',
        Cell: YesNoCell,
    },
    {
        columnKey: 'IsPaymentEnabled',
        Header: 'Payment enabled',
        accessor: 'IsPaymentEnabled',
        Cell: YesNoCell,
    },
];

export const createArticleColumns = (articleTypeNames: Map<number, string>) => [
    { columnKey: 'Id', Header: 'Id', accessor: 'Id' },
    { columnKey: 'Name', Header: 'Name', accessor: 'Name' },
    {
        columnKey: 'ArticleTypeId',
        Header: 'Type',
        accessor: 'ArticleTypeId',
        Cell: ({ cell }: ValueCellProps<number>) => (
            <>{formatArticleType(cell.value, articleTypeNames)}</>
        ),
    },
    {
        columnKey: 'Price',
        Header: 'Price',
        accessor: 'Price',
        Cell: ({ row }: ArticleRowCellProps) => <>{formatPrice(row.original)}</>,
    },
    { columnKey: 'VAT', Header: 'VAT', accessor: 'VAT', Cell: ValueOrMissingCell },
    { columnKey: 'Active', Header: 'Active', accessor: 'Active', Cell: YesNoCell },
    {
        columnKey: 'ServiceIds',
        Header: 'Linked service IDs',
        accessor: 'ServiceIds',
        Cell: ({ cell }: ValueCellProps<number[]>) => <>{formatServiceIds(cell.value)}</>,
    },
];

export const formatArticleType = (
    articleTypeId: number | null | undefined,
    articleTypeNames: Map<number, string>
) => {
    if (articleTypeId === undefined || articleTypeId === null) return MISSING_VALUE;

    return articleTypeNames.get(articleTypeId) ?? `#${articleTypeId}`;
};

export const formatPrice = (article: CompanyArticle) => {
    if (article.Price === undefined || article.Price === null) return MISSING_VALUE;

    const priceSignSuffix = article.PriceSign ? ` ${article.PriceSign}` : '';

    return `${article.Price}${priceSignSuffix}`;
};

export const formatServiceIds = (serviceIds: number[] | null | undefined) => {
    if (!serviceIds?.length) return MISSING_VALUE;

    return serviceIds.join(', ');
};
